import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Zap, Loader2, ArrowLeft, Apple, Gift, Eye, EyeOff, Bell, User, Building2 } from "lucide-react";
import { useSiteContent } from "@/context/SiteContentContext";
import { DEFAULT_SITE_CONTENT } from "@/lib/defaultSiteContent";
import { BrandMark } from "@/components/BrandMark";
import api, { getApiBase, getBackendRoot } from "@/lib/api";

export default function SellerAuth() {
  const { content } = useSiteContent();
  const auth = content.auth_page || DEFAULT_SITE_CONTENT.auth_page;
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refFromUrl = (searchParams.get("ref") || "").toUpperCase();
  const promoFromUrl = (searchParams.get("promo") || "").trim().toLowerCase();
  const [mode, setMode] = useState(refFromUrl || promoFromUrl || searchParams.get("register") ? "register" : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "", referral_code: refFromUrl });
  const [promoInfo, setPromoInfo] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [emailNotifyConsent, setEmailNotifyConsent] = useState(false);
  const [sellerType, setSellerType] = useState("private");
  const [oauth, setOauth] = useState({ google: null, apple: null });

  useEffect(() => {
    api
      .get("/auth/oauth-status")
      .then((r) => setOauth({ google: !!r.data.google, apple: !!r.data.apple }))
      .catch(() => setOauth({ google: false, apple: false }));
  }, []);

  useEffect(() => {
    if (refFromUrl) setForm((f) => ({ ...f, referral_code: refFromUrl }));
  }, [refFromUrl]);

  useEffect(() => {
    if (!promoFromUrl) {
      setPromoInfo(null);
      return;
    }
    setMode("register");
    api
      .get(`/auth/signup-promo/${encodeURIComponent(promoFromUrl)}`)
      .then((r) => setPromoInfo(r.data.promo))
      .catch(() => setPromoInfo(null));
  }, [promoFromUrl]);

  useEffect(() => {
    if (searchParams.get("register") === "ertesites") {
      setMode("register");
    }
  }, [searchParams]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const email = form.email.trim().toLowerCase();
      const password = form.password;
      if (mode === "login") await login(email, password);
      else {
        if (!emailNotifyConsent) {
          setError("Az üzenet-email értesítéshez hozzájárulás kötelező a regisztrációhoz.");
          setLoading(false);
          return;
        }
        await register(
          form.name.trim(),
          email,
          password,
          form.referral_code || undefined,
          emailNotifyConsent,
          promoFromUrl || undefined,
          sellerType,
        );
      }
      navigate("/fiok");
    } catch (err) {
      setError(err.response?.data?.detail || "Hiba történt. Próbáld újra.");
    } finally {
      setLoading(false);
    }
  };

  const oauthStart = (provider) => {
    const ok = provider === "google" ? oauth.google : oauth.apple;
    if (ok === false) {
      setError(
        provider === "google"
          ? "A Google bejelentkezés nincs beállítva a szerveren (GOOGLE_CLIENT_ID / GOOGLE_CLIENT_SECRET a backend .env-ben)."
          : "Az Apple bejelentkezés nincs beállítva a szerveren.",
      );
      return;
    }
    const root = getBackendRoot() || window.location.origin;
    const ref = (form.referral_code || refFromUrl || "").trim();
    const params = new URLSearchParams();
    if (ref) params.set("ref", ref);
    if (promoFromUrl) params.set("promo", promoFromUrl);
    params.set("origin", window.location.origin);
    if (mode === "register") params.set("email_consent", emailNotifyConsent ? "1" : "0");
    if (mode === "register" && sellerType === "dealer") params.set("seller_type", "dealer");
    window.location.href = `${root}/api/auth/${provider}/start?${params.toString()}`;
  };

  return (
    <div className="dark min-h-screen bg-anthracite-900 text-white flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-8">
          <ArrowLeft className="w-4 h-4" /> {auth.back_label}
        </Link>

        <div className="glass rounded-3xl p-8">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-6 h-6 text-electric" strokeWidth={1.5} />
            <span className="font-heading text-xl font-semibold"><BrandMark /></span>
          </div>
          <h1 className="font-heading text-2xl font-light mt-4">
            {mode === "login" ? auth.login_title : auth.register_title}
          </h1>
          <p className="text-slate-400 text-sm mt-1">{auth.subtitle}</p>

          {mode === "register" && promoInfo && (
            <div
              data-testid="signup-promo-banner"
              className="mt-4 flex items-start gap-2 rounded-xl bg-electric/10 border border-electric/30 p-3 text-sm"
            >
              <Gift className="w-4 h-4 text-electric shrink-0 mt-0.5" />
              <span className="text-slate-200">
                <b>{promoInfo.label}</b> — regisztráció után <b>{promoInfo.credits_target} kredit</b>.
                {promoInfo.duration_days} napig minden költés után az egyenleged automatikusan visszatöltődik{" "}
                {promoInfo.credits_target}-ra.
              </span>
            </div>
          )}
          {mode === "register" && !promoInfo && (
            <div data-testid="welcome-credit-banner" className="mt-4 flex items-start gap-2 rounded-xl bg-emerald-500/10 border border-emerald-400/25 p-3 text-sm">
              <Gift className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
              <span className="text-emerald-200" dangerouslySetInnerHTML={{ __html: auth.welcome_banner_html }} />
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 mt-6">
            <button
              data-testid="google-auth-btn"
              onClick={() => oauthStart("google")}
              className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-full bg-white text-slate-900 font-medium hover:bg-slate-100 transition-all"
            >
              <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4" />
              {auth.google_button_label}
            </button>
            <button
              type="button"
              data-testid="apple-auth-btn"
              onClick={() => oauthStart("apple")}
              className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-full bg-white text-slate-900 font-medium hover:bg-slate-100 transition-all"
            >
              <Apple className="w-4 h-4" /> {auth.apple_button_label}
            </button>
          </div>

          <div className="flex items-center gap-3 my-6">
            <div className="h-px flex-1 bg-white/10" />
            <span className="text-xs text-slate-500 uppercase tracking-widest">vagy email-lel</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <>
                <div>
                  <div className="text-xs tracking-[0.15em] uppercase text-slate-400 mb-2">Eladói profil</div>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      data-testid="auth-seller-type-private"
                      onClick={() => setSellerType("private")}
                      className={`rounded-xl p-3 border text-left text-xs transition-all ${
                        sellerType === "private"
                          ? "border-electric bg-electric/10"
                          : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04]"
                      }`}
                    >
                      <User className="w-4 h-4 text-electric mb-1" />
                      <div className="font-medium text-sm">Magán</div>
                    </button>
                    <button
                      type="button"
                      data-testid="auth-seller-type-dealer"
                      onClick={() => setSellerType("dealer")}
                      className={`rounded-xl p-3 border text-left text-xs transition-all ${
                        sellerType === "dealer"
                          ? "border-electric bg-electric/10"
                          : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04]"
                      }`}
                    >
                      <Building2 className="w-4 h-4 text-electric mb-1" />
                      <div className="font-medium text-sm">Kereskedés</div>
                      <div className="text-[10px] text-slate-500 mt-0.5">Publikus profil</div>
                    </button>
                  </div>
                </div>
                <Field testid="auth-name" label="Név" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
              </>
            )}
            <Field testid="auth-email" label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />
            <PasswordField
              testid="auth-password"
              label="Jelszó"
              value={form.password}
              onChange={(v) => setForm({ ...form, password: v })}
              show={showPassword}
              onToggleShow={() => setShowPassword((s) => !s)}
              required
            />

            {mode === "login" && (
              <p className="text-right -mt-1">
                <Link
                  to="/belepes/jelszo-elfelejtve"
                  data-testid="auth-forgot-password"
                  className="text-xs text-slate-400 hover:text-electric transition-colors"
                >
                  Elfelejtetted a jelszavad?
                </Link>
              </p>
            )}

            {mode === "register" && (
              <Field
                testid="auth-referral"
                label={refFromUrl ? "Meghívó kód (barátodtól kaptad)" : "Meghívó kód (opcionális)"}
                value={form.referral_code}
                onChange={(v) => setForm({ ...form, referral_code: v.toUpperCase() })}
              />
            )}

            {mode === "register" && searchParams.get("register") === "ertesites" && (
              <p
                data-testid="auth-email-consent-hint"
                className="text-sm text-amber-300/90 rounded-xl border border-amber-400/30 bg-amber-500/10 px-3 py-2"
              >
                Google / Apple regisztrációhoz pipáld be az email értesítés hozzájárulást.
              </p>
            )}

            {mode === "register" && (
              <label
                data-testid="auth-email-notify-consent"
                className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-3 cursor-pointer hover:border-electric/40 transition-colors"
              >
                <input
                  type="checkbox"
                  required
                  className="mt-1 h-4 w-4 rounded border-white/20 accent-electric"
                  checked={emailNotifyConsent}
                  onChange={(e) => setEmailNotifyConsent(e.target.checked)}
                />
                <span className="text-sm text-slate-300 leading-relaxed">
                  <span className="inline-flex items-center gap-1.5 text-white font-medium">
                    <Bell className="w-4 h-4 text-electric" strokeWidth={1.5} />
                    Email értesítés új chat üzenetről
                  </span>
                  <span className="block mt-1 text-slate-400 text-xs">
                    Hozzájárulok, hogy új érdeklődői üzenetről emailt küldjetek a regisztrált címemre, ha épp nem vagyok
                    bejelentkezve az oldalon.
                  </span>
                </span>
              </label>
            )}

            {error && <p data-testid="auth-error" className="text-sm text-red-400">{error}</p>}

            <button
              data-testid="auth-submit"
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full bg-electric hover:bg-electric-light disabled:opacity-50 text-white font-medium transition-all"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {mode === "login" ? "Belépés" : "Fiók létrehozása"}
            </button>
          </form>

          {mode === "register" && (
            <p className="text-xs text-slate-500 mt-4 text-center leading-relaxed">
              Regisztrációval elfogadod az{" "}
              <Link to="/aszf" className="text-electric hover:underline">ÁSZF-et</Link>
              {" "}és az{" "}
              <Link to="/adatvedelem" className="text-electric hover:underline">Adatvédelmi tájékoztatót</Link>.
            </p>
          )}

          <p className="text-sm text-slate-400 mt-5 text-center">
            {mode === "login" ? "Még nincs fiókod?" : "Van már fiókod?"}{" "}
            <button
              data-testid="auth-toggle"
              onClick={() => {
                setMode(mode === "login" ? "register" : "login");
                setError("");
              }}
              className="text-electric hover:text-electric-light"
            >
              {mode === "login" ? "Regisztrálj" : "Lépj be"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

const Field = ({ testid, label, value, onChange, type = "text", required }) => (
  <div>
    <label className="text-xs tracking-[0.15em] uppercase text-slate-400 block mb-2">{label}</label>
    <input
      data-testid={testid}
      type={type}
      required={required}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      autoCapitalize={type === "email" ? "none" : undefined}
      autoCorrect={type === "email" || type === "password" ? "off" : undefined}
      spellCheck={type === "email" || type === "password" ? false : undefined}
      className="w-full bg-white/[0.03] border border-white/10 rounded-xl p-3 text-sm text-white focus:border-electric outline-none transition-colors"
    />
  </div>
);

const PasswordField = ({ testid, label, value, onChange, show, onToggleShow, required }) => (
  <div>
    <label className="text-xs tracking-[0.15em] uppercase text-slate-400 block mb-2">{label}</label>
    <div className="relative">
      <input
        data-testid={testid}
        type={show ? "text" : "password"}
        required={required}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        className="w-full bg-white/[0.03] border border-white/10 rounded-xl p-3 pr-11 text-sm text-white focus:border-electric outline-none transition-colors"
      />
      <button
        type="button"
        data-testid="auth-password-toggle"
        onClick={onToggleShow}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-slate-400 hover:text-white"
        aria-label={show ? "Jelszó elrejtése" : "Jelszó megjelenítése"}
      >
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  </div>
);
